import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { fetchCompanyFromARES } from '../utils/ares';

const VerificationContext = createContext(null);

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const dbmToSignal = (gi) => {
  if (!gi || gi === 0) return null;
  const dbm = gi / 100;
  let bars, quality;
  if (dbm >= -65)       { bars = 5; quality = 'Výborný'; }
  else if (dbm >= -75)  { bars = 4; quality = 'Dobrý'; }
  else if (dbm >= -85)  { bars = 3; quality = 'Slabý'; }
  else if (dbm >= -95)  { bars = 2; quality = 'Velmi slabý'; }
  else if (dbm >= -110) { bars = 1; quality = 'Kritický'; }
  else                  { bars = 0; quality = 'Kritický'; }
  return { dbm, bars, quality };
};

const fetchWms = async (lat, lon, layer) => {
  const R = 6378137;
  const toRad = d => d * Math.PI / 180;
  const xC = R * toRad(lon);
  const yC = R * Math.log(Math.tan(Math.PI / 4 + toRad(lat) / 2));
  const half = 2500;
  const bbox = `${xC - half},${yC - half},${xC + half},${yC + half}`;
  const q = new URLSearchParams({
    REQUEST: 'GetFeatureInfo', SERVICE: 'WMS', VERSION: '1.1.1',
    info_format: 'application/json', feature_count: '1',
    X: '128', Y: '128', WIDTH: '256', HEIGHT: '256',
    SRS: 'EPSG:3857', BBOX: bbox,
    QUERY_LAYERS: `mc10:${layer}`, LAYERS: `mc10:${layer}`,
  });
  const res = await fetch(`https://vportal.ctu.gov.cz/geo/mc10/wms?${q}`);
  const data = await res.json();
  return data?.features?.[0]?.properties?.GRAY_INDEX ?? null;
};

export function VerificationProvider({ children, currentUserId }) {
  const currentUserIdRef = useRef(currentUserId);
  useEffect(() => { currentUserIdRef.current = currentUserId; }, [currentUserId]);

  const [task, setTask] = useState(() => {
    try {
      const saved = localStorage.getItem('bgVerificationTask');
      if (saved) {
        const t = JSON.parse(saved);
        if (t.status === 'running') t.status = 'paused'; // stránka byla obnovena
        return t;
      }
    } catch {}
    return null;
  });

  const taskRef = useRef(task);
  useEffect(() => { taskRef.current = task; }, [task]);

  const ctrlRef = useRef({ paused: true, stopped: true });
  const isLoopRunningRef = useRef(false);

  useEffect(() => {
    if (task) {
      localStorage.setItem('bgVerificationTask', JSON.stringify(task));
    } else {
      localStorage.removeItem('bgVerificationTask');
    }
  }, [task]);

  const runLoop = useCallback(async (taskSnapshot) => {
    if (isLoopRunningRef.current) return;
    isLoopRunningRef.current = true;

    const ctrl = ctrlRef.current;
    const opts = taskSnapshot.options;

    // Čistě _id-based cursor — nezávisí na updatedAt (který může být null pro neupravené firmy)
    // Max ObjectId ('fff...') = začni od začátku (nejvyšší _id)
    const MAX_OID = 'ffffffffffffffffffffffff';
    let pageCursor = { afterId: taskSnapshot.cursor?.afterId || MAX_OID };

    const progress = {
      processed:      taskSnapshot.progress?.processed      || 0,
      total:          taskSnapshot.progress?.total          || 0,
      icoSuccess:     taskSnapshot.progress?.icoSuccess     || 0,
      icoFailed:      taskSnapshot.progress?.icoFailed      || 0,
      addressSuccess: taskSnapshot.progress?.addressSuccess || 0,
      addressFailed:  taskSnapshot.progress?.addressFailed  || 0,
      signalSuccess:  taskSnapshot.progress?.signalSuccess  || 0,
      signalFailed:   taskSnapshot.progress?.signalFailed   || 0,
    };

    // Získat celkový počet firem (pokud ještě neznáme)
    if (!progress.total) {
      try {
        const r = await fetch(`${API_URL}/companies?userId=${taskSnapshot.ownerId}&limit=1`);
        if (r.ok) {
          const d = await r.json();
          progress.total = d.pagination?.totalCount || 0;
          setTask(prev => prev ? { ...prev, progress: { ...prev.progress, total: progress.total } } : prev);
        }
      } catch {}
    }

    let hasMore = true;
    let updateCounter = 0;

    try {
      while (hasMore && !ctrl.stopped && !ctrl.paused) {
        const url = `${API_URL}/companies?userId=${taskSnapshot.ownerId}&limit=100&afterId=${pageCursor.afterId}`;

        let data;
        try {
          const res = await fetch(url, { headers: { 'x-user-id': currentUserIdRef.current || '' } });
          if (!res.ok) { await sleep(3000); continue; }
          data = await res.json();
        } catch {
          await sleep(3000);
          continue;
        }

        const companies = data.companies || [];

        // Cursor endpoint vrací `hasMore`, standardní paginace vrací `pagination.hasNextPage`
        hasMore = data.hasMore !== undefined
          ? data.hasMore
          : (data.pagination?.hasNextPage || false);

        // Odvozeni kurzoru — cursor endpoint dá nextId/nextUpdatedAt, jinak vezmi posledni firmu
        const lastCompany = companies.length > 0 ? companies[companies.length - 1] : null;
        const nextPageCursor = { afterId: data.nextId || (lastCompany ? lastCompany._id : MAX_OID) };

        for (const company of companies) {
          if (ctrl.stopped || ctrl.paused) break;

          // Aktualizuj aktuálně zpracovávanou firmu
          setTask(prev => prev ? { ...prev, currentCompany: company.name || company.ico || '' } : prev);

          // === ARES (IČO + Adresa) ===
          const needAres = (opts.verifyIco || opts.verifyAddress) && company.ico;
          if (needAres) {
            try {
              const aresData = await fetchCompanyFromARES(company.ico.trim());
              const payload = {};
              if (opts.verifyIco && aresData.stav) payload.icoStatus = aresData.stav;
              if (opts.verifyAddress) {
                if (aresData.address) payload.address = aresData.address;
                if (aresData.kraj) payload.kraj = aresData.kraj;
              }
              if (Object.keys(payload).length > 0) {
                await fetch(`${API_URL}/companies/${company._id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', 'x-user-id': currentUserIdRef.current || '' },
                  body: JSON.stringify(payload),
                });
              }
              if (opts.verifyIco)      progress.icoSuccess++;
              if (opts.verifyAddress)  progress.addressSuccess++;
            } catch {
              if (opts.verifyIco)      progress.icoFailed++;
              if (opts.verifyAddress)  progress.addressFailed++;
            }
            await sleep(350);
          }

          // === Signál (Nominatim + WMS) ===
          if (opts.verifySignal && company.address && !ctrl.stopped && !ctrl.paused) {
            try {
              const geoRes = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&countrycodes=cz&addressdetails=1&limit=1&q=${encodeURIComponent(company.address)}`,
                { headers: { 'Accept-Language': 'cs', 'User-Agent': 'CRM-Verification/1.0' } }
              );
              const geoData = await geoRes.json();
              if (geoData.length) {
                const lat = parseFloat(geoData[0].lat);
                const lon = parseFloat(geoData[0].lon);
                const [giLte, gi5g] = await Promise.all([
                  fetchWms(lat, lon, 'cp_03_4g_d'),
                  fetchWms(lat, lon, 'cp_03_5g_d'),
                ]);
                const lteSignal  = dbmToSignal(giLte);
                const fiveGSignal = dbmToSignal(gi5g);
                const savedAt = new Date().toISOString();
                const signal = lteSignal
                  ? { bars: lteSignal.bars, quality: lteSignal.quality, dbm: lteSignal.dbm, savedAt }
                  : { bars: 0, quality: 'Neznámý', dbm: null, savedAt };
                const coverageSnapshot = {
                  savedAt, address: company.address,
                  lte:   lteSignal   ? { dbm: lteSignal.dbm,   quality: lteSignal.quality   } : null,
                  fiveG: fiveGSignal ? { dbm: fiveGSignal.dbm, quality: fiveGSignal.quality } : null,
                  curOp: null, curLte: null, curFiveG: null,
                };
                await fetch(`${API_URL}/companies/${company._id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', 'x-user-id': currentUserIdRef.current || '' },
                  body: JSON.stringify({ signal, coverageSnapshot }),
                });
                progress.signalSuccess++;
              } else {
                progress.signalFailed++;
              }
            } catch {
              progress.signalFailed++;
            }
            await sleep(1200);
          }

          progress.processed++;
          updateCounter++;

          // Updatuj state každých 5 firem
          if (updateCounter >= 5) {
            updateCounter = 0;
            const snap = { ...progress };
            setTask(prev => prev ? {
              ...prev,
              progress: snap,
              cursor: { ...pageCursor },
              lastUpdateAt: new Date().toISOString(),
            } : prev);
          }
        }

        // Posun kurzoru na další stránku (jen pokud jsme stránku dokončili)
        if (!ctrl.stopped && !ctrl.paused) {
          pageCursor = nextPageCursor;
        }
      }
    } finally {
      isLoopRunningRef.current = false;
    }

    const finalProgress = { ...progress };
    const now = new Date().toISOString();

    if (ctrl.stopped) {
      // stopVerification() již smazal task
    } else if (ctrl.paused) {
      setTask(prev => prev ? {
        ...prev,
        status: 'paused',
        progress: finalProgress,
        currentCompany: null,
        cursor: { ...pageCursor },
        lastUpdateAt: now,
      } : prev);
    } else {
      // Hotovo
      setTask(prev => prev ? {
        ...prev,
        status: 'completed',
        progress: finalProgress,
        currentCompany: null,
        cursor: null,
        completedAt: now,
        lastUpdateAt: now,
      } : prev);
    }
  }, []); // currentUserIdRef je stabilní ref, nepotřebujeme ho v deps

  const startVerification = useCallback((config) => {
    ctrlRef.current = { paused: false, stopped: false };
    const newTask = {
      id: Date.now(),
      ownerId:     config.ownerId,
      ownerName:   config.ownerName,
      options:     config.options,
      status:      'running',
      progress:    { processed: 0, total: 0, icoSuccess: 0, icoFailed: 0, addressSuccess: 0, addressFailed: 0, signalSuccess: 0, signalFailed: 0 },
      cursor:      null,
      currentCompany: null,
      startedAt:   new Date().toISOString(),
      lastUpdateAt: new Date().toISOString(),
    };
    setTask(newTask);
    setTimeout(() => runLoop(newTask), 0);
  }, [runLoop]);

  const pauseVerification = useCallback(() => {
    ctrlRef.current.paused = true;
    // Stav bude aktualizován smyčkou, až ji zastaví pause flag
  }, []);

  const resumeVerification = useCallback(() => {
    const current = taskRef.current;
    if (!current || current.status !== 'paused') return;
    ctrlRef.current = { paused: false, stopped: false };
    const resumed = { ...current, status: 'running' };
    setTask(resumed);
    setTimeout(() => runLoop(resumed), 150);
  }, [runLoop]);

  const stopVerification = useCallback(() => {
    ctrlRef.current.stopped = true;
    setTask(null);
  }, []);

  return (
    <VerificationContext.Provider value={{ task, startVerification, pauseVerification, resumeVerification, stopVerification }}>
      {children}
    </VerificationContext.Provider>
  );
}

export const useVerification = () => {
  const ctx = useContext(VerificationContext);
  if (!ctx) throw new Error('useVerification musí být použit uvnitř VerificationProvider');
  return ctx;
};
